import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FormatIndentIncreaseIcon from "@mui/icons-material/FormatIndentIncrease";
import BuildIcon from "@mui/icons-material/Build";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FlagIcon from "@mui/icons-material/Flag";
import DeleteIcon from "@mui/icons-material/Delete";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import EventIcon from "@mui/icons-material/Event";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import IosShareIcon from "@mui/icons-material/IosShare";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import NumbersIcon from "@mui/icons-material/Numbers";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";

import { getBrand } from "../../theme/brand";

const DRAWER_WIDTH = 312;

/**
 * Construye el árbol de navegación en base a `configuracion`.
 * Mantiene exactamente la misma estructura y condicionales que el menú anterior.
 */
function buildMenu(configuracion) {
  const destinatario = configuracion ? configuracion.destinatario : null;
  const esMovil = destinatario === 4 || destinatario === 6;

  return [
    {
      key: "servicios",
      label: "Servicios",
      icon: <AirportShuttleOutlinedIcon />,
      children: [
        { key: "tomaservicio", label: "Toma de Servicios", to: "/tomaservicio", icon: <PhoneInTalkOutlinedIcon /> },
        { key: "despachos", label: "Despachos", to: "/despachos", icon: <AirportShuttleOutlinedIcon /> },
        { key: "tripulaciones", label: "Tripulaciones", to: "/tripulaciones", icon: <SupervisorAccountOutlinedIcon /> },
        {
          key: "servicios-programados",
          label: "Servicios Programados",
          icon: <EventIcon />,
          children: [
            { key: "traslados", label: "Traslados", to: "/traslados" },
            { key: "eventos", label: "Eventos", to: "/eventos" },
            { key: "eliminados", label: "Eliminados", to: "/pendienteseliminados" },
          ],
        },
        { key: "pendientes", label: "Pendientes", to: "/serviciospendientes", icon: <AccessTimeIcon /> },
        { key: "finalizados", label: "Finalizados", to: "/serviciosfinalizados", icon: <FlagIcon /> },
        { key: "anulados", label: "Anulados", to: "/serviciosanulados", icon: <DeleteIcon /> },
        {
          key: "valorizacion",
          label: "Valorización de Servicios",
          icon: <MonetizationOnIcon />,
          children: [
            { key: "val-afiliado", label: "Valores Servicios Por Afiliado", to: "/valorizarservicios" },
            esMovil && { key: "val-movil", label: "Valores Servicios Por Móvil", to: "/valorizarserviciosmoviles" },
            { key: "val-feriados", label: "Feriados", to: "/feriados" },
          ].filter(Boolean),
        },
        { key: "prog-tripulaciones", label: "Programación de Tripulaciones", to: "/tripulacionesprogramacion", icon: <AccessTimeIcon /> },
      ],
    },
    {
      key: "configuracion",
      label: "Configuración",
      icon: <SettingsOutlinedIcon />,
      children: [
        { key: "numerointerno", label: "Número de Interno", to: "/numerointerno", icon: <PhoneOutlinedIcon /> },
        {
          key: "usuarios",
          label: "Usuarios",
          icon: <GroupOutlinedIcon />,
          children: [
            { key: "crear-usuarios", label: "Crear Usuarios", to: "/account", icon: <GroupAddIcon /> },
            { key: "accesos", label: "Accesos a Usuarios", to: "/accesos", icon: <GroupOutlinedIcon /> },
            { key: "password", label: "Cambiar Password", to: "/password", icon: <PublishedWithChangesIcon /> },
          ],
        },
        { key: "parametros", label: "Parámetros del Servicio", to: "/parametros", icon: <TuneOutlinedIcon /> },
        { key: "mitoalertas", label: "Mito Alertas", to: "/mitoalertas", icon: <NotificationsActiveOutlinedIcon /> },
        {
          key: "archivos",
          label: "Archivos",
          icon: <ArchiveOutlinedIcon />,
          children: [
            { key: "antecedentes", label: "Antecedentes", to: "/antecedentes" },
            { key: "tratamientos", label: "Tratamientos", to: "/tratamientos" },
            { key: "tiposservicios", label: "Tipos de servicios", to: "/tiposservicios" },
            { key: "motivosconsultas", label: "Motivos de Consulta", to: "/motivosconsultas" },
            { key: "diagnosticos", label: "Diagnósticos", to: "/diagnosticos" },
            { key: "medicos", label: "Médicos", to: "/medicos" },
            { key: "choferes", label: "Choferes", to: "/choferes" },
            { key: "enfermeros", label: "Enfermeros", to: "/enfermeros" },
            { key: "tiposmoviles", label: "Tipos de Móviles", to: "/tiposmoviles" },
            { key: "bases", label: "Bases Operativas", to: "/bases" },
            {
              key: "moviles",
              label: "Móviles",
              icon: <DirectionsCarFilledOutlinedIcon />,
              children: [
                { key: "movilespropios", label: "Propios", to: "/movilespropios" },
                { key: "movilesderivados", label: "Derivados", to: "/movilesderivados" },
              ],
            },
            { key: "telefonos", label: "Teléfonos", to: "/telefonos" },
            { key: "colores", label: "Colores", to: "/colores" },
            { key: "coseguros", label: "Coseguros", to: "/coseguros" },
            {
              key: "medicamentos",
              label: "Medicamentos",
              icon: <LocalPharmacyOutlinedIcon />,
              children: [
                { key: "almacenes", label: "Almacenes", to: "/almacenes" },
                { key: "medicamentos-abm", label: "Medicamentos", to: "/medicamentos" },
                { key: "movimientosmedicamentos", label: "Movimientos", to: "/movimientosmedicamentos" },
                { key: "stocksmedicamentos", label: "Stocks", to: "/stocksmedicamentos" },
                { key: "ajustesstocksmedicamentos", label: "Ajustes de Stock", to: "/ajustesstocksmedicamentos" },
                { key: "consumomedicamentos", label: "Consumo de Medicamentos", to: "/consumomedicamentos" },
              ],
            },
            { key: "topes", label: "Topes de Servicios", to: "/topes" },
            configuracion && configuracion.cargaConvenios
              ? { key: "cargaconvenios", label: "Carga de Convenios", to: "/cargaconvenios" }
              : null,
            configuracion && configuracion.institucionId && configuracion.whapi
              ? {
                  key: "recetas",
                  label: "Recetas",
                  children: [{ key: "medicosrecetas", label: "Médicos", to: "/medicosrecetas" }],
                }
              : null,
          ].filter(Boolean),
        },
      ],
    },
    {
      key: "listados",
      label: "Listados y Estadísticas",
      icon: <FormatIndentIncreaseIcon />,
      children: [
        { key: "listadosservicios", label: "Servicios", to: "/listadosservicios" },
        { key: "listadosdemoras", label: "Demoras", to: "/listadosdemoras" },
        { key: "listadoslaborales", label: "Laborales", to: "/listadoslaborales" },
        { key: "listadostraslados", label: "Traslados", to: "/listadostraslados" },
        { key: "listadostopes", label: "Topes Superados", to: "/listadostopes" },
        { key: "listadosconsumoservicios", label: "Consumo de Servicios", to: "/listadosconsumoservicios" },
        { key: "listadoshorariostripulacion", label: "Horarios Tripulación", to: "/listadoshorariostripulacion" },
        {
          key: "liquidaciones",
          label: "Liquidaciones",
          icon: <ReceiptLongOutlinedIcon />,
          children: [
            { key: "liq-medicos", label: "Liquidación Médicos", to: "/listadosliquidacionmedicos" },
            { key: "liq-choferes", label: "Liquidación Choferes", to: "/listadosliquidacionchoferes" },
            { key: "liq-enfermeros", label: "Liquidación Enfermeros", to: "/listadosliquidacionenfermeros" },
            { key: "liq-moviles", label: "Liquidación Móviles", to: "/listadosliquidacionmoviles" },
            { key: "liq-feriados", label: "Feriados", to: "/feriados" },
          ],
        },
        { key: "listadosprogramaciontripulaciones", label: "Programación de Tripulaciones", to: "/listadosprogramaciontripulaciones" },
        destinatario === 1 && { key: "listadosserviciosbases", label: "Servicios por Bases", to: "/listadosserviciosbases" },
        destinatario === 1 && { key: "listadosproductividad", label: "Listado de Productividad", to: "/listadosproductividad" },
        {
          key: "estadisticas",
          label: "Estadísticas",
          icon: <QueryStatsIcon />,
          children: [
            { key: "est-mesames", label: "Servicios Mes a Mes", to: "/estadisticaserviciosmesames" },
            { key: "est-semanales", label: "Servicios Semanales", to: "/estadisticaserviciossemanales" },
          ],
        },
        { key: "listadosmedicamentos", label: "Medicamentos", to: "/listadosmedicamentos" },
        {
          key: "exportar",
          label: "Exportar",
          icon: <IosShareIcon />,
          children: [{ key: "procesospami", label: "PAMI", to: "/procesospami" }],
        },
      ].filter(Boolean),
    },
    {
      key: "utiles",
      label: "Útiles",
      icon: <BuildIcon />,
      children: [
        { key: "tickets", label: "Tickets", to: "/tickets", icon: <ConfirmationNumberIcon /> },
        { key: "version", label: "Versión", to: "/version", icon: <NumbersIcon /> },
        destinatario !== 2 && { key: "qr", label: "WhatsApp QR", to: "/qr", icon: <QrCodeIcon /> },
        destinatario === 2 && { key: "qrcardio", label: "WhatsApp QR Cardio Cobranzas", to: "/qrcardio", icon: <QrCodeIcon /> },
        destinatario === 2 && { key: "qremersur", label: "WhatsApp QR Emersur Cobranzas", to: "/qremersur", icon: <QrCodeIcon /> },
        destinatario === 2 && { key: "qrcardiodespacho", label: "WhatsApp QR Cardio Despacho", to: "/qrcardiodespacho", icon: <QrCodeIcon /> },
        destinatario === 2 && { key: "qremersurdespacho", label: "WhatsApp QR Emersur Despacho", to: "/qremersurdespacho", icon: <QrCodeIcon /> },
        esMovil && { key: "tareas", label: "Tareas", to: "/tareas", icon: <AssignmentTurnedInOutlinedIcon /> },
      ].filter(Boolean),
    },
  ];
}

function MenuNode({ node, depth, activePath, openKeys, onToggle, onNavigate }) {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isOpen = !!openKeys[node.key];
  const isActive = !hasChildren && node.to && activePath === node.to;

  const handleClick = () => {
    if (hasChildren) {
      onToggle(node.key);
    } else if (node.to) {
      onNavigate(node.to);
    }
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        selected={isActive}
        sx={{
          pl: 2 + depth * 1.75,
          pr: 1.5,
          py: depth === 0 ? 1.1 : 0.75,
          borderRadius: 1.5,
          mx: 1,
          position: "relative",
          "&.Mui-selected": {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
            "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.18) },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 6,
              bottom: 6,
              width: 3,
              borderRadius: 3,
              bgcolor: "primary.main",
            },
          },
        }}
      >
        {node.icon ? (
          <ListItemIcon sx={{ minWidth: 38, color: isActive ? "primary.main" : "text.secondary" }}>
            {node.icon}
          </ListItemIcon>
        ) : (
          <Box sx={{ width: 14 }} />
        )}
        <ListItemText
          primary={node.label}
          primaryTypographyProps={{
            fontSize: depth === 0 ? 15 : 13.5,
            fontWeight: depth === 0 ? 700 : isActive ? 600 : 500,
            color: isActive ? "primary.main" : "#334155",
          }}
        />
        {hasChildren ? (isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />) : null}
      </ListItemButton>

      {hasChildren ? (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding dense>
            {node.children.map((child) => (
              <MenuNode
                key={child.key}
                node={child}
                depth={depth + 1}
                activePath={activePath}
                openKeys={openKeys}
                onToggle={onToggle}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Collapse>
      ) : null}
    </>
  );
}

function AppMenu({ open, onClose, configuracion }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState({});

  const items = useMemo(() => buildMenu(configuracion), [configuracion]);
  const brand = getBrand(configuracion);

  const handleToggle = (key) => {
    setOpenKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNavigate = (to) => {
    onClose();
    navigate(to);
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH, borderRight: "none" } }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          bgcolor: brand.color,
          color: "#fff",
        }}
      >
        <Box
          component="img"
          src={brand.logo}
          alt={brand.label}
          sx={{ height: 34, width: "auto", display: "block" }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.1 }}>Menú</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {brand.label}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }} aria-label="cerrar menú">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <List sx={{ py: 1, overflowY: "auto" }}>
        {items.map((node) => (
          <MenuNode
            key={node.key}
            node={node}
            depth={0}
            activePath={location.pathname}
            openKeys={openKeys}
            onToggle={handleToggle}
            onNavigate={handleNavigate}
          />
        ))}
      </List>
    </Drawer>
  );
}

export default AppMenu;
